import { useEffect, useState } from "react";
import { Network, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function RoleBadges({ roles }) {
  if (!roles || roles.length === 0)
    return <span className="text-xs text-slate-400">Hors plateforme</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <Badge key={r.code} variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-[10px]">
          {r.label}
        </Badge>
      ))}
    </div>
  );
}

export default function Intervenants() {
  const [data, setData] = useState({ parcours: [], supervision: [] });

  useEffect(() => {
    api.get("/meta/parcours").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="intervenants-page">
      <PageHeader title="Intervenants & rôles"
                  subtitle="Le parcours de la lame en 16 étapes et les intervenants de chaque étape." />

      <div className="flex items-center gap-3 p-4 rounded-lg mb-6 text-white" style={{ background: "#0F2942" }}>
        <Network className="h-6 w-6 text-sky-300" />
        <p className="text-sm">
          Chaque étape du cycle de vie du spécimen mobilise un ou plusieurs intervenants, associés à un
          rôle précis sur la plateforme (contrôle d'accès Loi 25).
        </p>
      </div>

      <Card className="border-slate-200 mb-6" data-testid="parcours-table-card">
        <Table className="clin-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead>Étape</TableHead>
              <TableHead>Intervenant</TableHead>
              <TableHead>Rôle plateforme</TableHead>
              <TableHead>Responsabilité</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="parcours-table-body">
            {data.parcours.map((p) => (
              <TableRow key={p.n} data-testid={`parcours-row-${p.n}`}>
                <TableCell className="font-mono text-xs text-slate-400">{p.n}</TableCell>
                <TableCell className="font-medium text-slate-800">{p.etape}</TableCell>
                <TableCell className="text-sm text-slate-600">{p.intervenants}</TableCell>
                <TableCell><RoleBadges roles={p.roles} /></TableCell>
                <TableCell className="text-xs text-slate-500 max-w-[320px]">{p.responsabilite}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <h2 className="font-head text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-teal-600" /> Supervision & gouvernance
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="supervision-grid">
        {data.supervision.map((s) => (
          <Card key={s.intervenant} className="p-5 border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-head font-semibold text-slate-800">{s.intervenant}</h3>
              <RoleBadges roles={s.roles} />
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{s.responsabilite}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}
